import { Field } from "@sitecore-jss/sitecore-jss-react";
import React, { useReducer } from "react";
import { useHistory } from "react-router-dom";

import { updateBankAccount } from "../../../api/mutation";
import { Policy } from "../../../api/policy";
import TextInput from "../../../components/InputFields/TextInput";
import { V5, V25 } from "../../../components/InputFields/validator/validators";
import { ModelState } from "../../../interfaces/modelState";
import { force } from "../../../typeguards/force";
import { MutationLayout } from "../MutationLayout";
import { bankAccountMutationSitecoreLabels } from "./BankAccountMutation.labels";
import { bankAccountMutationReducer, initialState } from "./BankAccountMutation.state";

interface Props {
  fields: Record<string, Field<string> & { url: string }>;
  policyNr: string;
  policy: Policy;
}

export const BankAccountMutation = ({ fields, policyNr, policy }: Props) => {
  const history = useHistory();

  const [{ data, loading, modelState }, dispatch] = useReducer(bankAccountMutationReducer, initialState);

  // create a copy of the object, because the fields try to mutate it
  const copyOfModelState = { ...modelState };

  const handleUpdateIban = (iban: string) => {
    dispatch({ type: "UPDATE_BANK_ACCOUNT_MUTATION_DATA", payload: { iban } });
  };

  const setModelState = (newModelState: ModelState) => {
    dispatch({ type: "UPDATE_BANK_ACCOUNT_MUTATION_ERROR", payload: newModelState });
  };

  const handleBankAccountMutationClick = async () => {
    try {
      const iban = force(data.iban);
      dispatch({ type: "UPDATE_BANK_ACCOUNT_MUTATION_FETCH_START" });
      const response = await updateBankAccount({ iban, policyNumber: policyNr });

      if (response.modelState) {
        dispatch({
          type: "UPDATE_BANK_ACCOUNT_MUTATION_FETCH_ERROR",
          payload: response.modelState,
        });
      } else {
        // remove the query parameters
        window.history.replaceState(null, "", window.location.pathname);
        history.push(fields.ThankYouPage.url);
      }
    } catch {
      dispatch({
        type: "UPDATE_BANK_ACCOUNT_MUTATION_FETCH_ERROR",
        payload: { global: ["Er is een onverwachte fout opgetreden. Probeer het opnieuw."] },
      });
    }
  };

  const canSubmitForm = Boolean(data.iban);

  const currentIBAN = policy.collections?.collection.map((c) => c.areaSEPA.iban);

  return (
    <MutationLayout
      canSubmitForm={canSubmitForm}
      handleSubmit={handleBankAccountMutationClick}
      loading={loading}
      modelState={copyOfModelState}
      title="IBAN wijzigen"
      CardContent={
        <>
          {currentIBAN?.length === 1 && (
            <TextInput
              fieldData={bankAccountMutationSitecoreLabels}
              name="CurrentIban"
              modelState={copyOfModelState}
              setModelState={setModelState}
              inputValue={currentIBAN[0]}
              validators={[V5, V25]}
              propertyName="model.CurrentIban"
              fieldName="CurrentIban"
              capitalize
              required
              disabled
            />
          )}
          <TextInput
            fieldData={bankAccountMutationSitecoreLabels}
            onBlur={handleUpdateIban}
            name="Iban"
            modelState={copyOfModelState}
            setModelState={setModelState}
            inputValue={data.iban}
            validators={[V5, V25]}
            propertyName="model.Iban"
            fieldName="Iban"
            capitalize
            required
          />
        </>
      }
      InfoContent={
        <>
          <h6>Verwerkingstermijn</h6>
          <p>
            Wijzigingen zijn niet direct zichtbaar in het DELAportaal De verwerkingstermijn bedraagt maximaal 2
            werkdagen. Er wordt <strong>geen</strong> nieuwe polis afgegeven.
          </p>

          <h6>Wijzigen betaaltermijn of betaalwijze</h6>
          <p>
            Een wijziging van de betaaltermijn of betaalwijze kun je hier niet doorgeven. Annuleer deze wijziging en
            kies onder ‘Polis wijzigen’ voor ‘Overig’. Geef hier de wijziging van de betaaltermijn of betaalwijze door.
          </p>
        </>
      }
    />
  );
};
